import React, { useEffect, useRef, useState } from "react";
import annotationPlugin from "chartjs-plugin-annotation";
import {
	Chart as ChartJS,
	LinearScale,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
} from "chart.js";
import { Bubble } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";
import { numberFormatter } from "../../services/utils";
import ResetZoomButton from "../../components/ResetZoomButton";
import useGraphFontSize from "../../hooks/useGraphFontSize";

ChartJS.register(
	annotationPlugin,
	LinearScale,
	PointElement,
	LineElement,
	Tooltip,
	Legend,
	zoomPlugin
);

const ScatterGraph = (props: any) => {
	const fontSize = useGraphFontSize();
	const chartRef = useRef<ChartJSOrUndefined<"bubble">>(null);
	const {
		graphData,
		handleScatterClick,
		selectedCustomer,
		thresholdX,
		thresholdY,
		filterExpansion,
	} = props;

	const [plottedData, setPlottedData] = useState([]);
	const [backgroundColor, setBackgroundColor] = useState([]);
	const [borderColor, setBorderColor] = useState([]);

	useEffect(() => {
		let data: any = [],
			background: any = [],
			BorderColor: any = [];
		graphData.forEach((d: any) => {
			data.push({
				x: d.revenueperdrop,
				y: d.ctsperdrop,
			});
			background.push(
				selectedCustomer === d?.CUSTOMER_ID
					? "#86BC2582"
					: selectedCustomer
						? "rgb(234, 166, 20, 0.4)"
						: "#EAA61482"
			);
			BorderColor.push(
				selectedCustomer === d?.CUSTOMER_ID
					? "#86BC2582"
					: selectedCustomer
						? "rgb(234, 166, 20, 0.4)"
						: "#EAA61482"
			);
		});
		setPlottedData(data);
		setBackgroundColor(background);
		setBorderColor(BorderColor);
	}, [graphData, selectedCustomer, filterExpansion]);

	const data = {
		datasets: [
			{
				label: "Gross Margin vs Delivery Margin",
				data: plottedData,
				backgroundColor: backgroundColor,
				borderColor: borderColor,
			},
		],
	};

	const zoomOptions = {
		pan: {
			enabled: true,
			mode: "xy",
			modifierKey: "ctrl",
		},
		zoom: {
			mode: "xy",
			drag: {
				enabled: true,
				borderColor: "rgb(54, 162, 235)",
				borderWidth: 1,
				backgroundColor: "rgba(54, 162, 235, 0.3)",
			},
		},
	};

	const options: any = {
		onClick: function (e: any, elements: any) {
			handleScatterClick(graphData[elements[0].index].CUSTOMER_ID);
		},
		responsive: true,
		maintainAspectRatio: false,
		resizeDelay: 0,
		pointRadius: 8,
		scales: {
			x: {
				border: {
					color: "black",
					width: 1,
				},
				type: "linear",
				position: "bottom",
				title: {
					display: true,
					text: "Revenue/Drop",
					color: "black",
					font: {
						size: fontSize,
					},
				},
				ticks: {
					display: true,
					callback: function (value: any, index: any, values: any) {
						if (value < 0) {
							return (
								"((" +
								numberFormatter(Math.abs(value), 0) +
								"))"
							);
						}
						return numberFormatter(value, 0);
					},
					font: {
						size: fontSize,
					},
					maxTicksLimit: 5,
					color: "black",
				},
				grid: {
					display: false,
				},
			},
			y: {
				border: {
					color: "black",
					width: 1,
				},
				type: "linear",
				position: "left",
				title: {
					display: true,
					text: "CTS Per Drop",
					color: "black",
					font: {
						size: fontSize,
					},
				},
				ticks: {
					display: true,
					maxTicksLimit: 10,
					color: "black",
					font: {
						size: fontSize,
					},
					callback: function (value: any) {
						return "$" + numberFormatter(value, 0);
					},

				},
				grid: {
					display: false,
				},
			},
		},
		plugins: {
			annotation: {
				annotations: {
					line1: {
						type: "line",
						yMin: thresholdY,
						yMax: thresholdY,
						borderDash: [4],
						borderColor: "#bebebe",
						borderWidth: [1],
						label: {
							content: `CTS: $${numberFormatter(
								Math.round(thresholdY)
							)}`,
							display: true,
							position: "end",
							yAdjust: -10,
							font: {
								size: fontSize,
								weight: 400,
							},
							backgroundColor: "transparent",
							borderWidth: 0,
							padding: 0,
							color: "black",
						},
					},
					line2: {
						type: "line",
						xMin: thresholdX,
						xMax: thresholdX,
						borderDash: [4],
						borderColor: "#bebebe",
						borderWidth: [1],
						font: {
							size: fontSize,
							weight: 5,
						},
						label: {
							content: `Dropsize Threshold:${numberFormatter(
								Math.round(thresholdX)
							)}`,
							xAdjust: 40,
							display: true,
							position: "start",
							font: {
								size: fontSize,
								weight: 400,
							},
							backgroundColor: "transparent",
							borderWidth: 0,
							padding: 0,
							color: "black",
						},
					},
				},
			},
			zoom: zoomOptions,
			tooltip: {
				callbacks: {
					label: function (context: any) {
						let element = graphData[context.dataIndex];
						return ["Customer: " + element.customer_name,
						`Revenue Per Drop: ${numberFormatter(
							element.revenueperdrop.toFixed(2)
						)}`,
						`CTS Per Drop: ${numberFormatter(
							element.ctsperdrop.toFixed(2)
						)}`,
						];
					},
					labelTextColor: function (context: any) {
						return "white";
					},
					labelFont: function (context: any) {
						return "10";
					},
				},
				displayColors: false,
				backgroundColor: "rgb(0, 0, 0, 0.7)",
				titleFont: {
					size: 12,
					color: "#000000",
				},
			},
			legend: {
				display: false,
			},
		},
	};

	const handleResetZoom = () => {
		if (chartRef.current) {
			chartRef.current.resetZoom();
		}
	};

	return (
		<div className="relative bg-[white] w-full h-full">
			<ResetZoomButton onClick={handleResetZoom} />{" "}
			{/* Use the common component */}
			<div className="w-full h-full">
				<Bubble
					key={filterExpansion.toString()}
					ref={chartRef}
					options={{
						...options,
						responsive: true,
						maintainAspectRatio: false,
					}}
					data={data}
				/>
			</div>
		</div>
	);
};

export default ScatterGraph;
